import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import moment from 'moment';
import { getProductTitle } from '../../utils/salesUtils';

const cellWrapSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
};

const badgeSx = {
  textAlign: 'center',
  borderRadius: 5,
  padding: '3px 8px',
  whiteSpace: 'nowrap',
};

/**
 * Sales listesinde "İade sürecinde" rozeti. Tıklanınca planlanan iadenin
 * (hangi ürün, ne zaman, neden) detayını modalda gösterir.
 */
const SalePendingRefundCell = ({ sale }) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const pending = sale?.pendingRefund;

  if (!pending || typeof pending !== 'object') {
    return (
      <Box sx={cellWrapSx}>
        <span style={{ color: '#999' }}>-</span>
      </Box>
    );
  }

  if (pending.status === 'done') {
    return (
      <Box sx={cellWrapSx}>
        <Box
          sx={{
            ...badgeSx,
            backgroundColor: '#2e7d32',
          }}
        >
          <span style={{ color: 'white', fontSize: 12 }}>
            {pending.message || translate('sales.refund_success', { _: 'İade süreci başarılı' })}
          </span>
        </Box>
      </Box>
    );
  }

  const failed = pending.status === 'failed';
  const waiting = pending.status === 'waiting';
  const items = Array.isArray(pending.items) ? pending.items : [];

  const label = failed
    ? pending.message ||
      translate('sales.refund_next_day_failed', {
        _: 'Ertesi gün iade başarısız',
      })
    : waiting
      ? translate('sales.refund_pending', { _: 'İade bekliyor' })
      : translate('sales.refund_in_progress', { _: 'İade sürecinde' });

  return (
    <>
      <Box sx={cellWrapSx}>
        <Box
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          sx={{
            ...badgeSx,
            backgroundColor: failed ? '#d32f2f' : '#ed6c02',
            cursor: 'pointer',
          }}
        >
          <span style={{ color: 'white', fontSize: 12 }}>{label}</span>
        </Box>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{translate('sales.planned_refund', { _: 'Planlanan İade' })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {failed
              ? 'Ertesi gün otomatik iade denemeleri başarısız oldu. Hata detayı aşağıda.'
              : 'Bu satış için iade ertesi güne planlandı; sistem otomatik gerçekleştirecek.'}
          </Typography>

          <Box sx={{ my: 1 }}>
            <Typography variant="body2">
              <strong>Durum:</strong>{' '}
              {failed
                ? pending.message || 'Ertesi gün iade başarısız'
                : waiting
                  ? 'İade bekliyor'
                  : 'Bekliyor / planlandı'}
            </Typography>
            <Typography variant="body2">
              <strong>Planlanan zaman:</strong>{' '}
              {pending.scheduledFor
                ? moment(pending.scheduledFor).format('LL LT')
                : '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Tahmini tutar:</strong> {pending.amount ?? '—'} TL
            </Typography>
            <Typography variant="body2">
              <strong>Kapsam:</strong>{' '}
              {pending.isFull ? 'Tüm satış' : 'Ürün bazlı'}
            </Typography>
            {pending.reason ? (
              <Typography variant="body2">
                <strong>Not:</strong> {pending.reason}
              </Typography>
            ) : null}
            {pending.createdBy ? (
              <Typography variant="body2">
                <strong>Planlayan:</strong> {pending.createdBy}
              </Typography>
            ) : null}
            {failed && pending.lastError ? (
              <Typography variant="body2" sx={{ color: 'error.main' }}>
                <strong>Hata:</strong> {pending.lastError}
              </Typography>
            ) : null}
          </Box>

          {items.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                İade edilecek ürünler
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ürün</TableCell>
                    <TableCell>Kategori</TableCell>
                    <TableCell align="right">Adet</TableCell>
                    <TableCell align="right">Birim</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((it, idx) => (
                    <TableRow key={`${it.productId}-${idx}`}>
                      <TableCell>
                        {getProductTitle(it) || it.productId}
                      </TableCell>
                      <TableCell>{it.groupKey}</TableCell>
                      <TableCell align="right">{it.count}</TableCell>
                      <TableCell align="right">{it.price} TL</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SalePendingRefundCell;
